import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {trackFilteredProcedureFlowchartView} from 'procedures/procedureAnalyticsEventsUtil';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

interface ProcedureMethodSectionFilterDropdown {
  filterableSectionOptions: Array<FilteredProcedureMethodSection>;
  selectedMethodSectionFilter: Array<FilteredProcedureMethodSection>;
  setSelectedMethodSectionFilter: (value: Array<FilteredProcedureMethodSection>) => void;
  procedureId: string;
}

export enum FilteredProcedureMethodSection {
  PARAMETERS = 'PARAMETERS',
  MATERIALS = 'MATERIALS',
  EQUIPMENT = 'EQUIPMENT',
}

export const ALL_METHOD_SECTION_OPTIONS = [
  FilteredProcedureMethodSection.PARAMETERS,
  FilteredProcedureMethodSection.MATERIALS,
  FilteredProcedureMethodSection.EQUIPMENT,
];

export function getSectionOptionUIString(section: FilteredProcedureMethodSection): string {
  switch (section) {
    case FilteredProcedureMethodSection.PARAMETERS:
      return 'Parameters';
    case FilteredProcedureMethodSection.MATERIALS:
      return 'Materials';
    case FilteredProcedureMethodSection.EQUIPMENT:
      return 'Equipment';
    default:
      assert(false, 'unsupported method section filter option');
  }
}

function getMenuLabel(
  isDropdownOpen: boolean,
  selectedMethodSectionFilter: Array<FilteredProcedureMethodSection>,
  allSections: Array<FilteredProcedureMethodSection>
): string {
  switch (selectedMethodSectionFilter.length) {
    case allSections.length:
      return 'View: All';
    case 0:
      return 'View: None';
    case 1:
      return `View: ${getSectionOptionUIString(selectedMethodSectionFilter[0])}`;
    default:
      selectedMethodSectionFilter.sort((a, b) => allSections.indexOf(a) - allSections.indexOf(b));
      return `View: ${getSectionOptionUIString(selectedMethodSectionFilter[0])} + ${
        selectedMethodSectionFilter.length - 1
      }`;
  }
}

export default function ProcedureMethodSectionFilterDropdown(
  props: ProcedureMethodSectionFilterDropdown
): JSX.Element {
  const {
    filterableSectionOptions: allSections,
    selectedMethodSectionFilter,
    setSelectedMethodSectionFilter,
  } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const {procedureType} = ProcedurePageContext.useContext();

  const updateMethodFilterOnSelection = (value: FilteredProcedureMethodSection): void => {
    let newSelectedMethodSectionFilter;
    if (selectedMethodSectionFilter.includes(value)) {
      newSelectedMethodSectionFilter = selectedMethodSectionFilter.filter((v) => v !== value);
    } else {
      newSelectedMethodSectionFilter = [...selectedMethodSectionFilter, value];
    }
    setSelectedMethodSectionFilter(newSelectedMethodSectionFilter);
    trackFilteredProcedureFlowchartView(
      props.procedureId,
      procedureType,
      value,
      newSelectedMethodSectionFilter
    );
  };

  return (
    <Dropdown
      closeOnInsideClick={false}
      closeOnOutsideClick={true}
      isOpen={isDropdownOpen}
      onToggle={() => {
        setIsDropdownOpen(!isDropdownOpen);
      }}
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.SMALL}
          isMulti={true}
          value={selectedMethodSectionFilter}
        >
          {allSections.map((value) => (
            <Option key={value} value={value} onSelect={updateMethodFilterOnSelection}>
              {getSectionOptionUIString(value)}
            </Option>
          ))}
        </Menu>
      }
      alwaysDropDown={true}
    >
      <Button
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        text={getMenuLabel(isDropdownOpen, selectedMethodSectionFilter, allSections)}
        isDropdown={true}
        className={css(styles.floatingFilter, isDropdownOpen && styles.droppedMenu)}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  floatingFilter: {
    zIndex: 2000,
    background: WHITE,
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    position: 'absolute',
    top: '16px',
    left: '16px',
  },
  droppedMenu: {
    background: GRAY_LIGHT,
  },
});
